import { useMemo, useCallback } from "react";
import { cond, matches, always, get, update, add, identity, clamp, pipe, set } from "lodash/fp";
import { positionDefault } from "@reach/popover";
import { positionTopLeft, positionTopCenter, positionTopRight, positionTopFullWidth, positionBottomLeft, positionBottomCenter, positionBottomRight, positionBottomFullWidth, positionLeftTop, positionLeftCenter, positionLeftBottom, positionLeftFullHeight, positionRightTop, positionRightCenter, positionRightBottom, positionRightFullHeight } from "../utils/popover-position.es.js";
import { adjustPlacement } from "../utils/adjust-placement.es.js";
const positionMap = {
  top: {
    start: positionTopLeft,
    center: positionTopCenter,
    end: positionTopRight,
    stretch: positionTopFullWidth
  },
  bottom: {
    start: positionBottomLeft,
    center: positionBottomCenter,
    end: positionBottomRight,
    stretch: positionBottomFullWidth
  },
  left: {
    start: positionLeftTop,
    center: positionLeftCenter,
    end: positionLeftBottom,
    stretch: positionLeftFullHeight
  },
  right: {
    start: positionRightTop,
    center: positionRightCenter,
    end: positionRightBottom,
    stretch: positionRightFullHeight
  }
};
const transformOrigin = cond([
  [matches({ placement: "top", justify: "start" }), always("left bottom")],
  [matches({ placement: "top", justify: "stretch" }), always("left bottom")],
  [matches({ placement: "top", justify: "center" }), always("center bottom")],
  [matches({ placement: "top", justify: "end" }), always("right bottom")],
  [matches({ placement: "left", justify: "start" }), always("right top")],
  [matches({ placement: "left", justify: "stretch" }), always("right top")],
  [matches({ placement: "left", justify: "center" }), always("right center")],
  [matches({ placement: "left", justify: "end" }), always("right bottom")],
  [matches({ placement: "right", justify: "start" }), always("left top")],
  [matches({ placement: "right", justify: "stretch" }), always("left top")],
  [matches({ placement: "right", justify: "center" }), always("left center")],
  [matches({ placement: "right", justify: "end" }), always("left bottom")],
  [matches({ placement: "bottom", justify: "start" }), always("left top")],
  [matches({ placement: "bottom", justify: "stretch" }), always("left top")],
  [matches({ placement: "bottom", justify: "center" }), always("center top")],
  [matches({ placement: "bottom", justify: "end" }), always("right top")]
]);
function usePopoverPosition({
  placement,
  justify,
  offset,
  buffer,
  preventOverflow,
  centerWhenSmall
}) {
  const adjustPlacementMemo = useMemo(
    () => adjustPlacement(placement, buffer, preventOverflow),
    [buffer, placement, preventOverflow]
  );
  return useCallback(
    (targetRect, popoverRect) => {
      const adjustedPlacement = adjustPlacementMemo(targetRect, popoverRect);
      const dimension = placement === "left" || placement === "right" ? "height" : "width";
      const adjustedJustify = centerWhenSmall && popoverRect[dimension] <= targetRect[dimension] ? "center" : justify;
      const basePosition = get([adjustedPlacement, adjustedJustify], positionMap) || positionDefault;
      const shiftByOffset = {
        top: update("top", add(-offset)),
        bottom: update("top", add(offset)),
        left: update("left", add(-offset)),
        right: update("left", add(offset))
      }[adjustedPlacement] || identity;
      const clampToViewport = placement === "left" || placement === "right" ? update("top", clamp(0, window.innerHeight - popoverRect.height)) : update("left", clamp(0, window.innerWidth - popoverRect.width));
      return pipe(
        basePosition,
        shiftByOffset,
        clampToViewport,
        set(
          "transformOrigin",
          transformOrigin({
            placement: adjustedPlacement,
            justify: adjustedJustify
          })
        )
      )(targetRect, popoverRect);
    },
    [adjustPlacementMemo, centerWhenSmall, justify, offset, placement]
  );
}
export {
  usePopoverPosition
};
